import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createChangelogEntry,
  deleteChangelogEntry,
  getChangelogEntries,
  getChangelogStatus,
  importChangelog,
  setChangelogVisibility,
  updateChangelogEntry,
} from './AdminAPI';

const emptyDraft = (currentVersion = '') => ({
  id: null,
  section: currentVersion ? `Version ${currentVersion}` : '',
  text: '',
});

export default function AdminChangelog({ onStatusChange }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [currentVersion, setCurrentVersion] = useState('');
  const [draft, setDraft] = useState(emptyDraft());
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [loadedEntries, status] = await Promise.all([
        getChangelogEntries(),
        getChangelogStatus(),
      ]);
      setEntries(loadedEntries || []);
      if (status?.currentVersion) {
        setCurrentVersion(status.currentVersion);
        setDraft((current) => (current.section ? current : emptyDraft(status.currentVersion)));
      }
    } catch (loadError) {
      setError('The changelog could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sections = useMemo(() => entries.reduce((result, entry) => {
    const section = entry.section || 'Other';
    if (!result[section]) result[section] = [];
    result[section].push(entry);
    return result;
  }, {}), [entries]);

  const sectionNames = useMemo(() => Object.keys(sections), [sections]);

  const notifyStatusChange = () => {
    if (onStatusChange) onStatusChange();
  };

  const toggle = async (entry) => {
    setSavingId(entry.id);
    setError('');
    try {
      await setChangelogVisibility(entry.id, !entry.visible);
      setEntries((current) => current.map((item) => (
        item.id === entry.id ? { ...item, visible: !item.visible } : item
      )));
      notifyStatusChange();
    } catch (saveError) {
      setError('Visibility could not be saved. The previous value is unchanged.');
    } finally {
      setSavingId(null);
    }
  };

  const saveCustom = async (event) => {
    event.preventDefault();
    const payload = { section: draft.section.trim(), text: draft.text.trim() };
    if (!payload.section || !payload.text) {
      setError('Enter both a section and changelog text.');
      return;
    }

    setSavingId('form');
    setError('');
    try {
      if (draft.id) {
        const updated = await updateChangelogEntry(draft.id, payload);
        setEntries((current) => current.map((entry) => (
          entry.id === updated.id ? updated : entry
        )));
      } else {
        const created = await createChangelogEntry(payload);
        setEntries((current) => [...current, created]);
      }
      setDraft(emptyDraft(currentVersion));
      notifyStatusChange();
    } catch (saveError) {
      setError('The custom changelog entry could not be saved. Your text is unchanged.');
    } finally {
      setSavingId(null);
    }
  };

  const editCustom = (entry) => {
    setDraft({ id: entry.id, section: entry.section, text: entry.text });
    setError('');
  };

  const removeCustom = async (entry) => {
    if (!window.confirm(`Delete “${entry.text}”?`)) return;

    setSavingId(entry.id);
    setError('');
    try {
      await deleteChangelogEntry(entry.id);
      setEntries((current) => current.filter((item) => item.id !== entry.id));
      if (draft.id === entry.id) setDraft(emptyDraft(currentVersion));
      notifyStatusChange();
    } catch (deleteError) {
      setError('The custom changelog entry could not be deleted.');
    } finally {
      setSavingId(null);
    }
  };

  const sync = async () => {
    setSavingId('import');
    setError('');
    try {
      await importChangelog();
      await load();
      notifyStatusChange();
    } catch (syncError) {
      setError('The deployment changelog could not be synchronized.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <main className="admin-changelog" aria-busy={loading}>
      <div className="admin-changelog__header">
        <div>
          <p>Choose which entries are visible on the public changelog page.</p>
        </div>
        <button type="button" className="button-primary" onClick={sync} disabled={loading || savingId !== null}>
          {savingId === 'import' ? 'Synchronizing…' : 'Synchronize now'}
        </button>
      </div>
      <form className="admin-changelog__form" onSubmit={saveCustom}>
        <h2>{draft.id ? 'Edit custom entry' : 'Add custom entry'}</h2>
        <label htmlFor="changelog-section">
          Version or section
          <input
            id="changelog-section"
            list="changelog-sections"
            maxLength="120"
            value={draft.section}
            onChange={(event) => setDraft({ ...draft, section: event.target.value })}
            disabled={savingId !== null}
            required
          />
        </label>
        <datalist id="changelog-sections">
          {sectionNames.map((section) => <option key={section} value={section} />)}
        </datalist>
        <label htmlFor="changelog-text">
          Changelog text
          <textarea
            id="changelog-text"
            rows="3"
            maxLength="500"
            value={draft.text}
            onChange={(event) => setDraft({ ...draft, text: event.target.value })}
            disabled={savingId !== null}
            required
          />
        </label>
        <p>New custom entries are deactivated until you check them below.</p>
        <div className="admin-changelog__form-actions">
          <button type="submit" className="button-primary" disabled={savingId !== null}>
            {savingId === 'form' ? 'Saving…' : draft.id ? 'Save entry' : 'Add entry'}
          </button>
          {draft.id && (
            <button
              type="button"
              className="button-secondary"
              onClick={() => setDraft(emptyDraft(currentVersion))}
              disabled={savingId !== null}
            >
              Cancel editing
            </button>
          )}
        </div>
      </form>
      {loading && <p role="status">Loading changelog…</p>}
      {error && <p className="admin-changelog__error" role="alert">{error}</p>}
      {!loading && entries.length === 0 && <p>No changelog entries have been imported yet.</p>}
      {Object.entries(sections).map(([section, sectionEntries]) => (
        <section key={section} className="admin-changelog__section">
          <h2>{section}</h2>
          <ul>
            {sectionEntries.map((entry) => (
              <li key={entry.id}>
                <div className="admin-changelog__entry-row">
                  <label className="admin-changelog__entry">
                    <input
                      type="checkbox"
                      checked={entry.visible}
                      onChange={() => toggle(entry)}
                      disabled={savingId !== null}
                    />
                    <span>{entry.text}</span>
                  </label>
                  {entry.source === 'custom' && (
                    <div className="admin-changelog__entry-actions">
                      <button
                        type="button"
                        className="button-secondary"
                        onClick={() => editCustom(entry)}
                        disabled={savingId !== null}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="button-critical"
                        onClick={() => removeCustom(entry)}
                        disabled={savingId !== null}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </main>
  );
}
